/* engine/cabinetCuts.js — the CAD-level cabinet CUT/ADAPTATION engine
 * (2026-07-22 mandate v2: "a real geometry adaptation system based on actual
 * wall geometry and actual cabinet geometry", arbitrary angles, polygon cuts,
 * multiple walls, per-part clipping).
 *
 * MODEL — a cut is a VERTICAL PRISM in cabinet-local space, stored per
 * INSTANCE (non-destructive; the technician master never changes):
 *
 *   config.cuts = [{ id, kind: "prism", points: [[x,z],...](mm),
 *                    yFrom(mm), yTo(mm) }]
 *
 * Local frame (cabinetMath parity): x across width (0 = centre, +x right),
 * z across depth (0 = centre, +z FRONT face), y up with 0 = the BODY BOTTOM.
 * Arbitrary polygons → 90°/45°/any-angle corners, columns, returns,
 * wall-following boundaries, multi-wall combinations (one cut per wall) and
 * stepped slopes (several prisms with different yFrom).
 * The legacy config.topCut (4-corner notch) is read through the same path
 * (converted on the fly), so older projects keep rendering.
 *
 * GEOMETRY — real booleans via polygon-clipping (martinez), never visual
 * tricks:
 *   • deep parts (carcass/counter/kick): the part's plan rectangle minus the
 *     active cut polygons, split into VERTICAL BANDS at each cut's y-limits →
 *     each band extrudes to a valid solid (correct from every camera).
 *   • thin FRONT panels (doors/drawer fronts): the cut prism crosses the
 *     panel's z-slab over an x-interval → the face profile (x,y) minus those
 *     rectangles; panels the prism misses stay untouched ("do not blindly cut
 *     the entire cabinet").
 * All maths in metres, world-space accurate; the config boundary speaks mm. */
import { WALL_THICKNESS } from "./constants";
import pc from "polygon-clipping";

const MM = 1 / 1000;
const EPS = 1e-6;

/* ── polygon helpers ─────────────────────────────────────────────────────── */
export function ringArea(ring) {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return s / 2;
}
export function multiPolyArea(mp) {
  let s = 0;
  (mp || []).forEach((poly) => poly.forEach((ring) => { s += ringArea(ring); }));
  return Math.abs(s);
}
const rectRing = (x1, z1, x2, z2) => [[x1, z1], [x2, z1], [x2, z2], [x1, z2]];

/* ── normalized cuts (metres, local) ─────────────────────────────────────── */
let _cutSeq = 0;
export function newCutId() { return "cut-" + (++_cutSeq) + "-" + Math.random().toString(36).slice(2, 6); }

/** PARAMETRIC GOLA CUT (2026-08-24m user mandate: "Cut → GOLA Cut — real
 *  parametric mm values, referenced from an edge, like Cabinet Vision").
 *  Stored per instance as {id, kind:"gola", ref:"L"|"R", xMm, zMm, wMm, hMm,
 *  dMm, enabled} — xMm = lateral offset of the cut from the reference edge,
 *  zMm = start height from the body bottom, wMm/hMm the cut size and dMm the
 *  POCKET depth behind the door plane (see GOLA_CUT_DOOR_ZONE_M below, so
 *  18 × 18 is the standard notch). golaCutRectM resolves it
 *  against the CURRENT def, so the cut re-anchors when the module resizes
 *  and moves with it — the ONE geometry every view + the CSG consume. */
/** THE DOOR ZONE: a front sits 22mm proud of the carcass front face, so the
 *  gola pocket is machined BEHIND it — the same plane the automatic notch and
 *  the profile itself use (golaRuns.GOLA_FACE_RECESS_M). */
export const GOLA_CUT_DOOR_ZONE_M = 0.022;

export function golaCutRectM(c, def) {
  const W = (def && def.width) || 0, D = (def && def.depth) || 0, H = (def && def.height) || 0;
  const num = (v, d0) => { const n = Number(v); return Number.isFinite(n) ? n : d0; };
  const xOff = Math.max(0, num(c.xMm, 0)) * MM;
  const w = Math.max(0.001, num(c.wMm, 18) * MM);
  const d = Math.max(0.001, num(c.dMm, 18) * MM);
  const y1 = Math.max(0, num(c.zMm, 0) * MM);
  const y2 = Math.min(H > 0 ? H : 100, y1 + Math.max(0.001, num(c.hMm, 100000) * MM));
  const over = 0.001;
  let x1, x2;
  if (String(c.ref || "R") === "L") {
    x1 = -W / 2 + xOff; x2 = x1 + w;
    if (xOff === 0) x1 -= over;             // flush → cut cleanly through the skin
  } else {
    x2 = W / 2 - xOff; x1 = x2 - w;
    if (xOff === 0) x2 += over;
  }
  /* DEPTH IS THE POCKET (2026-08-24 user: "the gola place is 18 × 18 — you
   * need to do it with depth; it looks like it's going from the door and it's
   * big"): d is measured from the DOOR-BACK plane, exactly like the standard
   * notch, so 18 × 18 machines the standard pocket. The cut still opens
   * through the front face (nothing is left floating in front of it). */
  return { x1, x2, z1: D / 2 - GOLA_CUT_DOOR_ZONE_M - d, z2: D / 2 + over, y1, y2 };
}

/** cutsOf(cfg, def) → [{id, points [[x,z] m], yFrom m, yTo m}] — includes the
 *  legacy topCut converted to an equivalent prism. Invalid entries dropped. */
export function cutsOf(cfg, def) {
  const H = (def && def.height) || 0;
  const W = (def && def.width) || 0;
  const D = (def && def.depth) || 0;
  const out = [];
  ((cfg && cfg.cuts) || []).forEach((c) => {
    if (!c) return;
    // parametric GOLA cut → the standard prism form, flagged golaNotch so the
    // door/drawer fronts are NEVER clipped by it (carcass-only by contract)
    if (c.kind === "gola") {
      if (c.enabled === false) return;
      const r = golaCutRectM(c, def);
      if (!(r.x2 - r.x1 > EPS) || !(r.y2 - r.y1 > EPS)) return;
      out.push({
        id: c.id || newCutId(),
        points: [[r.x1, r.z1], [r.x2, r.z1], [r.x2, r.z2], [r.x1, r.z2]],
        yFrom: r.y1, yTo: r.y2,
        golaNotch: true, golaCut: true,
      });
      return;
    }
    if (!Array.isArray(c.points) || c.points.length < 3) return;
    const pts = c.points.map((p) => [Number(p[0]) * MM, Number(p[1]) * MM]);
    if (pts.some((p) => !Number.isFinite(p[0]) || !Number.isFinite(p[1]))) return;
    if (Math.abs(ringArea(pts)) < 1e-8) return;
    const yFrom = Number.isFinite(Number(c.yFrom)) ? Number(c.yFrom) * MM : 0;
    const yTo = Number.isFinite(Number(c.yTo)) ? Number(c.yTo) * MM : 100;
    if (yTo - yFrom < EPS) return;
    out.push({ id: c.id || newCutId(), points: pts, yFrom, yTo });
  });
  const tc = cfg && cfg.topCut;
  if (tc && tc.enabled) {
    const w = Math.max(0, (Number(tc.cutW) || 0) * MM);
    const d = Math.max(0, (Number(tc.cutD) || 0) * MM);
    const h = Math.max(0, (Number(tc.cutH) || 0) * MM);
    if (w > 0 && d > 0 && h > 0 && W > 0 && D > 0) {
      const left = String(tc.corner || "back-left").endsWith("left");
      const front = String(tc.corner || "").startsWith("front");
      const x1 = left ? -W / 2 : W / 2 - w, x2 = left ? -W / 2 + w : W / 2;
      const z1 = front ? D / 2 - d : -D / 2, z2 = front ? D / 2 : -D / 2 + d;
      out.push({ id: "legacy-topcut", points: rectRing(x1, z1, x2, z2), yFrom: Math.max(0, H - h), yTo: 100 });
    }
  }
  return out;
}

/* ── deep-part clipping: plan region × vertical bands ────────────────────── */
/** clipPartPlan(rect {x1,z1,x2,z2}, yBottom, yTop, cuts)
 *  → null (untouched) | { bands: [{y0, h, full?:true, region?:MultiPolygon}] }
 *  y values in the SAME local frame as the cuts (0 = body bottom). */
export function clipPartPlan(rect, yBottom, yTop, cuts) {
  const rectPoly = [[rectRing(rect.x1, rect.z1, rect.x2, rect.z2)]];
  const rectArea = (rect.x2 - rect.x1) * (rect.z2 - rect.z1);
  const touching = (cuts || []).filter((c) => {
    if (c.yTo <= yBottom + EPS || c.yFrom >= yTop - EPS) return false;
    try { return multiPolyArea(pc.intersection(rectPoly, [[c.points]])) > 1e-9; }
    catch (e) { return false; }
  });
  if (!touching.length) return null;
  const bounds = new Set([yBottom, yTop]);
  touching.forEach((c) => {
    if (c.yFrom > yBottom + EPS && c.yFrom < yTop - EPS) bounds.add(c.yFrom);
    if (c.yTo > yBottom + EPS && c.yTo < yTop - EPS) bounds.add(c.yTo);
  });
  const ys = [...bounds].sort((a, b) => a - b);
  const bands = [];
  for (let i = 0; i < ys.length - 1; i++) {
    const y0 = ys[i], y1 = ys[i + 1];
    if (y1 - y0 < EPS) continue;
    const active = touching.filter((c) => c.yFrom <= y0 + EPS && c.yTo >= y1 - EPS);
    if (!active.length) { bands.push({ y0, h: y1 - y0, full: true }); continue; }
    let region;
    try { region = pc.difference(rectPoly, ...active.map((c) => [[c.points]])); }
    catch (e) { region = rectPoly; }
    const area = multiPolyArea(region);
    if (area >= rectArea - 1e-9) bands.push({ y0, h: y1 - y0, full: true });
    else if (area > 1e-9) bands.push({ y0, h: y1 - y0, region });
    // area ≈ 0 → the whole band is removed: emit nothing
  }
  // untouched ONLY if every band is full AND they cover the whole part height
  // (a fully-swallowed band emits nothing — the remaining full bands must not
  // read as "no clipping happened" or the caller would re-emit the full box).
  const covered = bands.reduce((s, b) => s + b.h, 0);
  if (bands.every((b) => b.full) && covered >= (yTop - yBottom) - EPS) return null;
  return { bands };
}

/* ── thin FRONT panels: face-profile holes ───────────────────────────────── */
/** clipFacePanel(xMin, xMax, yBottom, yTop, zMin, zMax, cuts)
 *  → null | { holes: [{x1,y1,x2,y2}] } in the SAME cabinet-local coords.
 *  A vertical prism crossing the panel's thin z-slab removes the x-interval
 *  it covers, over the prism's y-range — exact for the 20mm panel depth. */
export function clipFacePanel(xMin, xMax, yBottom, yTop, zMin, zMax, cuts) {
  const slab = [[rectRing(xMin, zMin, xMax, zMax)]];
  const holes = [];
  (cuts || []).forEach((c) => {
    if (c.yTo <= yBottom + EPS || c.yFrom >= yTop - EPS) return;
    let inter;
    try { inter = pc.intersection(slab, [[c.points]]); } catch (e) { return; }
    if (multiPolyArea(inter) < 1e-10) return;
    let x1 = Infinity, x2 = -Infinity;
    inter.forEach((poly) => poly.forEach((ring) => ring.forEach(([x]) => { x1 = Math.min(x1, x); x2 = Math.max(x2, x); })));
    const y1 = Math.max(yBottom, c.yFrom), y2 = Math.min(yTop, c.yTo);
    if (x2 - x1 > EPS && y2 - y1 > EPS) holes.push({ x1, y1, x2, y2 });
  });
  if (!holes.length) return null;
  // does anything remain? (a hole can swallow the whole face)
  const facePoly = [[rectRing(xMin, yBottom, xMax, yTop)]];
  let remain;
  try { remain = pc.difference(facePoly, ...holes.map((h) => [[rectRing(h.x1, h.y1, h.x2, h.y2)]])); }
  catch (e) { remain = facePoly; }
  return { holes, remain, gone: multiPolyArea(remain) < 1e-10 };
}

/* ── AUTOMATIC WALL FIT — from the real wall engine geometry ─────────────── */
// 2026-08-18: was a divergent 0.10m literal while the whole engine uses
// WALL_THICKNESS (0.15 default, per-wall override) — auto wall-fit cuts sat
// 25mm off the real wall face.

function wallStripRing(w, grow) {
  const t = ((w.thickness || WALL_THICKNESS) / 2) + grow;
  const dx = w.end[0] - w.start[0], dz = w.end[1] - w.start[1];
  const L = Math.hypot(dx, dz) || 1;
  const ux = dx / L, uz = dz / L;
  const nx = -uz, nz = ux;
  const ax = w.start[0] - ux * grow, az = w.start[1] - uz * grow;
  const bx = w.end[0] + ux * grow, bz = w.end[1] + uz * grow;
  return [
    [ax + nx * t, az + nz * t], [ax - nx * t, az - nz * t],
    [bx - nx * t, bz - nz * t], [bx + nx * t, bz + nz * t],
  ];
}

/** computeWallFitCuts(pos, eff, walls, opts) → proposed cuts (mm, ready to
 *  store) — ONE cut per intersecting wall, each the EXACT intersection polygon
 *  of the wall body (grown by clearance) with the cabinet plan rectangle, in
 *  cabinet-local coordinates. Arbitrary wall angles produce the true angled
 *  boundary; several walls (tight corners, returns) each contribute a cut. */
/** cutFrontConflict(cfg, def) → null | { gone, holes } — does any cut cross
 *  the FRONT FACE (doors / drawer fronts)? Reuses clipFacePanel with the
 *  engine's own front-plane band, so the warning the Module Editor shows is
 *  EXACTLY what the geometry engine will clip (§20: never silently delete
 *  front geometry — say it). gone=true ⇒ the whole front band is swallowed. */
export function cutFrontConflict(cfg, def) {
  // gola cuts NEVER clip the fronts (carcass-only by contract) — no warning
  const cuts = cutsOf(cfg, def).filter((c) => !c.golaNotch);
  if (!cuts.length || !def) return null;
  const W = def.width || 0, H = def.height || 0, D = def.depth || 0;
  if (!(W > 0 && H > 0 && D > 0)) return null;
  const fd = D / 2 - 0.012;                       // engine front plane (cabinetMath _fd)
  const fc = clipFacePanel(-(W - 0.02) / 2, (W - 0.02) / 2, 0.015, H - 0.015, fd - 0.01, fd + 0.01, cuts);
  if (!fc) return null;
  return { gone: !!fc.gone, holes: (fc.holes || []).length };
}

/** cutAffectedSummary(cfg, def) → null | ["carcass","plinth","worktop",
 *  "fronts…"] — WHICH physical parts the instance cuts actually intersect,
 *  using the ENGINE'S OWN part rectangles/bands (cabinetMath constants), so
 *  the Cuts tab tells the user exactly what will be clipped (§13/§14). */
export function cutAffectedSummary(cfg, def) {
  const cuts = cutsOf(cfg, def);
  if (!cuts.length || !def) return null;
  const W = def.width || 0, H = def.height || 0, D = def.depth || 0;
  if (!(W > 0 && H > 0 && D > 0)) return null;
  const hit = (rect, lo, hi) => !!clipPartPlan(rect, lo, hi, cuts);
  const out = [];
  const cW = W - 0.008, cH = H - 0.008, cD = D - 0.025;      // cabinetMath carcass
  if (hit({ x1: -cW / 2, z1: -cD / 2, x2: cW / 2, z2: cD / 2 }, H / 2 - cH / 2, H / 2 + cH / 2)) out.push("carcass");
  const fd = D / 2 - 0.012;
  // gola cuts never touch the plinth (2026-08-24n) — the summary must agree
  const nonGola = cuts.filter((c) => !c.golaNotch);
  if (nonGola.length && clipPartPlan({ x1: -(W - 0.02) / 2, z1: fd, x2: (W - 0.02) / 2, z2: fd + 0.02 }, 0, 0.08, nonGola)) out.push("plinth");
  const slabD = D + 0.02;
  if (hit({ x1: -W / 2, z1: 0.01 - slabD / 2, x2: W / 2, z2: 0.01 + slabD / 2 }, H, H + 0.038)) out.push("worktop");
  const fc = cutFrontConflict(cfg, def);
  if (fc) out.push(fc.gone ? "fronts (a panel is fully removed)" : "fronts (clipped with matching holes)");
  return out.length ? out : null;
}

export function computeWallFitCuts(pos, eff, walls, opts = {}) {
  if (!pos || !eff) return [];
  const W = eff.width || 0, D = eff.depth || 0, H = eff.height || 0;
  if (W <= 0 || D <= 0) return [];
  const grow = (opts.clearanceMm != null ? opts.clearanceMm : 2) * MM;   // 0 is a valid clearance
  const cosA = Math.cos(pos.angle), sinA = Math.sin(pos.angle);
  const toLocal = (x, z) => {
    const rx = x - pos.cx, rz = z - pos.cz;
    return [cosA * rx - sinA * rz, sinA * rx + cosA * rz];
  };
  const cabPoly = [[rectRing(-W / 2, -D / 2, W / 2, D / 2)]];
  const out = [];
  (walls || []).forEach((w) => {
    if (!w || !w.start || !w.end || w.type === "arc") return;
    // gate on the UNGROWN wall body: a wall that merely touches the cabinet
    // (sub-3mm graze) must not propose a clearance-only sliver cut.
    let raw;
    try { raw = pc.intersection(cabPoly, [[wallStripRing(w, 0).map((c) => toLocal(c[0], c[1]))]]); } catch (e) { return; }
    let rw = 0, rd = 0;
    if (raw && raw.length) {
      let x1 = Infinity, x2 = -Infinity, z1 = Infinity, z2 = -Infinity;
      raw.forEach((poly) => poly.forEach((ring) => ring.forEach(([x, z]) => { x1 = Math.min(x1, x); x2 = Math.max(x2, x); z1 = Math.min(z1, z); z2 = Math.max(z2, z); })));
      rw = x2 - x1; rd = z2 - z1;
    }
    if (!raw || multiPolyArea(raw) < 1e-6 || Math.min(rw, rd) < 0.003) return;
    const strip = [wallStripRing(w, grow).map((c) => toLocal(c[0], c[1]))];
    let inter;
    try { inter = pc.intersection(cabPoly, [strip]); } catch (e) { return; }
    if (!inter || multiPolyArea(inter) < 1e-6) return;
    // one cut per resulting region (a wall crossing a corner can split in two)
    inter.forEach((poly) => {
      const ring = poly[0];
      if (!ring || Math.abs(ringArea(ring)) < 1e-6) return;
      let x1 = Infinity, x2 = -Infinity, z1 = Infinity, z2 = -Infinity;
      ring.forEach(([x, z]) => { x1 = Math.min(x1, x); x2 = Math.max(x2, x); z1 = Math.min(z1, z); z2 = Math.max(z2, z); });
      out.push({
        id: newCutId(),
        kind: "prism",
        points: ring.slice(0, ring.length - 1).map(([x, z]) => [Math.round(x / MM), Math.round(z / MM)]),
        yFrom: 0,                                    // a wall is full-height; raise for soffits/beams
        yTo: Math.round((H + 1) / MM),
        wallId: w.id,
        widthMm: Math.round((x2 - x1) / MM),
        depthMm: Math.round((z2 - z1) / MM),
        angleDeg: Math.round((Math.atan2(w.end[1] - w.start[1], w.end[0] - w.start[0]) - pos.angle) * 180 / Math.PI * 10) / 10,
      });
    });
  });
  return out;
}

/** Stepped SLOPE helper: approximate an inclined cut ceiling by N prisms with
 *  descending yFrom across the polygon's x-extent (real geometry, adjustable
 *  resolution — the engine primitive stays the vertical prism). */
export function slopedCutSteps(points, yFromA, yFromB, steps = 6) {
  let x1 = Infinity, x2 = -Infinity;
  points.forEach(([x]) => { x1 = Math.min(x1, x); x2 = Math.max(x2, x); });
  const out = [];
  for (let i = 0; i < steps; i++) {
    const sx1 = x1 + ((x2 - x1) * i) / steps;
    const sx2 = x1 + ((x2 - x1) * (i + 1)) / steps;
    let zMin = Infinity, zMax = -Infinity;
    points.forEach(([, z]) => { zMin = Math.min(zMin, z); zMax = Math.max(zMax, z); });
    out.push({
      id: newCutId(), kind: "prism",
      points: rectRing(sx1, zMin, sx2, zMax),
      yFrom: yFromA + ((yFromB - yFromA) * (i + 0.5)) / steps,
      yTo: 1e6,
    });
  }
  return out;
}
